import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaArrowRight, FaBoxes, FaBullhorn, FaCheck, FaEnvelope, FaFacebook, FaGlobe, FaGoogle, FaImage,
  FaKey, FaPalette, FaPercent, FaSave, FaStore, FaTags, FaTrash
} from 'react-icons/fa';
import AuthContext from '../../../../utils/secure-route/AuthContext';
import useApi from '../../../../utils/api-manager/Helper/useApi';
import { useToastHandler } from '../../../common/ToastHandler';
import ContentManagement from './ContentManagement';
import './StorefrontStudio.css';

const THEMES = [
  { key: 'teal', name: 'Fresh Teal', primary: '#088F8F', accent: '#91DDCF', text: '#0f172a', bg: '#f0fdfa' },
  { key: 'indigo', name: 'Premium Indigo', primary: '#4f46e5', accent: '#a5b4fc', text: '#111827', bg: '#eef2ff' },
  { key: 'emerald', name: 'Retail Green', primary: '#059669', accent: '#a7f3d0', text: '#064e3b', bg: '#ecfdf5' },
  { key: 'rose', name: 'Boutique Rose', primary: '#e11d48', accent: '#fecdd3', text: '#111827', bg: '#fff1f2' },
  { key: 'amber', name: 'Warm Amber', primary: '#d97706', accent: '#fde68a', text: '#111827', bg: '#fffbeb' },
  { key: 'slate', name: 'Modern Slate', primary: '#334155', accent: '#cbd5e1', text: '#0f172a', bg: '#f8fafc' },
  { key: 'cyan', name: 'Clean Cyan', primary: '#0891b2', accent: '#a5f3fc', text: '#164e63', bg: '#ecfeff' },
  { key: 'violet', name: 'Soft Violet', primary: '#7c3aed', accent: '#ddd6fe', text: '#1f2937', bg: '#f5f3ff' },
  { key: 'lime', name: 'Grocery Lime', primary: '#65a30d', accent: '#d9f99d', text: '#1a2e05', bg: '#f7fee7' },
  { key: 'coral', name: 'Friendly Coral', primary: '#ea580c', accent: '#fed7aa', text: '#111827', bg: '#fff7ed' },
];

const THEME_TYPES = [
  { key: 'classic', name: 'Classic Store', description: 'General product catalog and ecommerce storefront.' },
  { key: 'quick-commerce', name: 'Quick Commerce', description: 'Fast grocery and essentials delivery experience.' },
  { key: 'food-delivery', name: 'Food Delivery', description: 'Restaurant discovery, menus, and online ordering.' },
  { key: 'service-brand', name: 'Service Brand', description: 'Services, portfolio, contact, and quotation requests.' },
];

const defaultSettings = (storeId) => ({
  store: storeId || '',
  theme_type: 'classic',
  theme_key: 'teal',
  show_all_products: true,
  featured_product_ids: [],
  featured_category_names: [],
  discount_type: 'none',
  discount_value: '0',
  discount_label: '',
  enable_global_discount: false,
  show_deal_section: true,
  show_category_section: true,
  show_about_section: true,
  show_contact_section: true,
  header_cta_text: '',
  header_cta_link: '',
  announcement_text: '',
  seo_title: '',
  seo_description: '',
});

const defaultSocialSettings = () => ({
  google_enabled: false,
  google_client_id: '',
  google_client_secret: '',
  has_google_secret: false,
  facebook_enabled: false,
  facebook_client_id: '',
  facebook_client_secret: '',
  facebook_redirect_uri: '',
  has_facebook_secret: false,
});

const EMAIL_PURPOSES = [
  { value: 'password_reset', label: 'Password Reset', hint: 'Sends the "reset your password" link to customers.' },
  { value: 'order_communication', label: 'Order & Customer Communication', hint: 'Order confirmations and other customer-facing emails.' },
  { value: 'general', label: 'General / Other', hint: 'Fallback for anything without its own account above, if you only want to set up one.' },
];

const defaultEmailPurposeRow = (purpose) => ({
  purpose,
  label: EMAIL_PURPOSES.find(p => p.value === purpose)?.label || purpose,
  enabled: false,
  provider: 'custom',
  smtp_host: '',
  smtp_port: 587,
  smtp_use_tls: true,
  smtp_use_ssl: false,
  smtp_username: '',
  smtp_password: '',
  has_smtp_password: false,
  from_email: '',
  from_name: '',
});

const defaultEmailUsage = () => ({
  emails_sent: 0,
  free_tier_limit: 100,
  emails_remaining: 100,
});

const getProductId = (product) => String(product?.id || product?.barcode || product?.code_sku || product?.product_id || '');
const getProductName = (product) => product?.title || product?.product_name || product?.name || product?.barcode || 'Unnamed product';
const getProductCategory = (product) => {
  const assigned = product?.categories?.assigned;
  if (Array.isArray(assigned) && assigned.length) return assigned[assigned.length - 1]?.name || 'General';
  return product?.category_name || product?.category || 'General';
};
const studioProductCache = new Map();
const studioProductRequests = new Map();
// Separate cache for the "Products to show" picker below, keyed by
// store+page+search - this list is fetched a page at a time from the
// server instead of loading the whole catalog into the browser, so it
// stays usable for stores with thousands of products.
const studioProductPickCache = new Map();
const PRODUCT_PICKER_PAGE_SIZE = 20;

const StorefrontStudio = () => {
  const { user } = useContext(AuthContext);
  const storeId = user?.store;
  const { Get, Post, Put, Patch, GetPaginatedData, getAPI } = useApi();
  const { handleToast } = useToastHandler();

  const [activePanel, setActivePanel] = useState('overview');
  const [settingsId, setSettingsId] = useState(null);
  const [settings, setSettings] = useState(defaultSettings(storeId));
  const [products, setProducts] = useState([]);
  const [pickerProducts, setPickerProducts] = useState([]);
  const [pickerPage, setPickerPage] = useState(1);
  const [pickerTotalCount, setPickerTotalCount] = useState(0);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearchInput, setPickerSearchInput] = useState('');
  const [pickerSearch, setPickerSearch] = useState('');
  const [domains, setDomains] = useState([]);
  const [storeConfigId, setStoreConfigId] = useState(null);
  const [newDomain, setNewDomain] = useState('');
  const [editingDomain, setEditingDomain] = useState(null);
  const [editingDomainValue, setEditingDomainValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [socialSettings, setSocialSettings] = useState(defaultSocialSettings());
  const [socialSaving, setSocialSaving] = useState(false);

  const [emailPurposes, setEmailPurposes] = useState(EMAIL_PURPOSES.map(p => defaultEmailPurposeRow(p.value)));
  const [emailUsage, setEmailUsage] = useState(defaultEmailUsage());
  const [emailSavingPurpose, setEmailSavingPurpose] = useState(null);

  const selectedTheme = useMemo(
    () => THEMES.find(theme => theme.key === settings.theme_key) || THEMES[0],
    [settings.theme_key]
  );

  const categories = useMemo(() => {
    return Array.from(new Set(products.map(getProductCategory).filter(Boolean))).sort();
  }, [products]);

  useEffect(() => {
    if (!storeId) return;
    setSettings(defaultSettings(storeId));
    loadStudioData();
  }, [storeId]);

  const loadStudioData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchStorefrontSettings(), fetchStoreDomains(), fetchProducts(), fetchSocialLoginSettings(), fetchEmailSettings()]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSocialLoginSettings = async () => {
    try {
      const response = await Get(`${getAPI('social_login_settings')}?store_id=${storeId}`);
      const data = response?.data || response;
      if (data && data.id) {
        setSocialSettings({ ...defaultSocialSettings(), ...data, google_client_secret: '', facebook_client_secret: '' });
      } else {
        setSocialSettings(defaultSocialSettings());
      }
    } catch (error) {
      setSocialSettings(defaultSocialSettings());
    }
  };

  const updateSocialSetting = (key, value) => {
    setSocialSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSocialSettings = async () => {
    if (!storeId) {
      handleToast({ type: 'ERROR', message: 'Store ID not found' });
      return;
    }
    setSocialSaving(true);
    try {
      const payload = { ...socialSettings, store: storeId };
      // Never send blank secret fields - blank means "leave the saved one alone".
      if (!payload.google_client_secret) delete payload.google_client_secret;
      if (!payload.facebook_client_secret) delete payload.facebook_client_secret;

      const response = await Post('social_login_settings', payload);
      const data = response?.data || response;
      setSocialSettings({ ...defaultSocialSettings(), ...data, google_client_secret: '', facebook_client_secret: '' });
      handleToast({ type: 'SUCCESS', message: 'Social login settings saved' });
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to save social login settings';
      handleToast({ type: 'ERROR', message });
    } finally {
      setSocialSaving(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await Get(`${getAPI('email_settings')}?store_id=${storeId}`);
      const data = response?.data || response;
      const purposes = Array.isArray(data?.purposes) ? data.purposes : [];
      if (purposes.length) {
        setEmailPurposes(EMAIL_PURPOSES.map(p => {
          const row = purposes.find(r => r.purpose === p.value);
          return row ? { ...defaultEmailPurposeRow(p.value), ...row, smtp_password: '' } : defaultEmailPurposeRow(p.value);
        }));
      } else {
        setEmailPurposes(EMAIL_PURPOSES.map(p => defaultEmailPurposeRow(p.value)));
      }
      setEmailUsage(data?.usage ? { ...defaultEmailUsage(), ...data.usage } : defaultEmailUsage());
    } catch (error) {
      setEmailPurposes(EMAIL_PURPOSES.map(p => defaultEmailPurposeRow(p.value)));
      setEmailUsage(defaultEmailUsage());
    }
  };

  const updateEmailPurpose = (purpose, key, value) => {
    setEmailPurposes(prev => prev.map(row => row.purpose === purpose ? { ...row, [key]: value } : row));
  };

  const saveEmailPurpose = async (purpose) => {
    if (!storeId) {
      handleToast({ type: 'ERROR', message: 'Store ID not found' });
      return;
    }
    const row = emailPurposes.find(r => r.purpose === purpose);
    if (!row) return;
    setEmailSavingPurpose(purpose);
    try {
      const payload = { ...row, store: storeId };
      // Never send a blank password - blank means "leave the saved one alone".
      if (!payload.smtp_password) delete payload.smtp_password;
      delete payload.label;
      delete payload.has_smtp_password;

      const response = await Post('email_settings', payload);
      const data = response?.data || response;
      updateEmailPurpose(purpose, 'smtp_password', '');
      setEmailPurposes(prev => prev.map(r => r.purpose === purpose ? { ...defaultEmailPurposeRow(purpose), ...data, smtp_password: '' } : r));
      handleToast({ type: 'SUCCESS', message: `${row.label} email settings saved` });
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to save email settings';
      handleToast({ type: 'ERROR', message });
    } finally {
      setEmailSavingPurpose(null);
    }
  };

  const fetchStorefrontSettings = async () => {
    try {
      const response = await Get(`${getAPI('storefront_settings')}?store_id=${storeId}`);
      const data = response?.data || response;
      if (data && data.id) {
        setSettingsId(data.id);
        setSettings({ ...defaultSettings(storeId), ...data, store: data.store || storeId });
      } else {
        setSettingsId(null);
        setSettings(defaultSettings(storeId));
      }
    } catch (error) {
      setSettingsId(null);
      setSettings(defaultSettings(storeId));
    }
  };

  const fetchStoreDomains = async () => {
    try {
      const response = await Get(`${getAPI('ecom_store')}?store_id=${storeId}&domain=${window.location.hostname}`);
      const data = Array.isArray(response) ? response.find(item => item.store_id === storeId || item.store === storeId) : response;
      setStoreConfigId(data?.id || null);
      setDomains(Array.isArray(data?.urls) ? data.urls : []);
    } catch (error) {
      setStoreConfigId(null);
      setDomains([]);
    }
  };

  const fetchProducts = async () => {
    const cacheKey = String(storeId || '');
    if (studioProductCache.has(cacheKey)) {
      setProducts(studioProductCache.get(cacheKey));
      return;
    }
    try {
      if (!studioProductRequests.has(cacheKey)) {
        // This full list only feeds the category-chip picker above, so it
        // still needs every product's category regardless of catalog size.
        // A single page-1 request used to silently miss any product past
        // whatever page_size the server capped the response to - loop
        // through every page instead (bounded at 100 pages as a backstop).
        const fetchAllPages = async () => {
          const allProducts = [];
          let page = 1;
          let hasMore = true;
          while (hasMore && page <= 100) {
            const response = await GetPaginatedData('publicProducts', {
              page,
              search: '',
              queryParams: { store_id: storeId, page_size: 1000 }
            });
            const pageProducts = response?.data || [];
            allProducts.push(...pageProducts);
            hasMore = Boolean(response?.nextPage) && pageProducts.length > 0;
            page += 1;
          }
          return allProducts;
        };
        studioProductRequests.set(cacheKey, fetchAllPages().finally(() => studioProductRequests.delete(cacheKey)));
      }
      const data = await studioProductRequests.get(cacheKey);
      studioProductCache.set(cacheKey, data);
      setProducts(data);
    } catch (error) {
      setProducts([]);
    }
  };

  // Server-side paginated fetch for the "Products to show" picker. Unlike
  // fetchProducts() above (which pulls up to 1000 products just to derive
  // the category chip list), this only ever holds one page of results in
  // memory, so a store with thousands of products stays fast and the list
  // doesn't silently cut off at whatever cap was hardcoded.
  const fetchPickerProducts = async (page, search) => {
    if (!storeId) return;
    const cacheKey = `${storeId}|${page}|${search}`;
    setPickerLoading(true);
    try {
      if (studioProductPickCache.has(cacheKey)) {
        const cached = studioProductPickCache.get(cacheKey);
        setPickerProducts(cached.data);
        setPickerTotalCount(cached.totalCount);
        return;
      }
      const response = await GetPaginatedData('publicProducts', {
        page,
        search,
        queryParams: { store_id: storeId, page_size: PRODUCT_PICKER_PAGE_SIZE }
      });
      const result = { data: response?.data || [], totalCount: response?.totalCount || 0 };
      studioProductPickCache.set(cacheKey, result);
      setPickerProducts(result.data);
      setPickerTotalCount(result.totalCount);
    } catch (error) {
      setPickerProducts([]);
      setPickerTotalCount(0);
    } finally {
      setPickerLoading(false);
    }
  };

  useEffect(() => {
    if (!storeId) return;
    fetchPickerProducts(pickerPage, pickerSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, pickerPage, pickerSearch]);

  // Debounce the search box so every keystroke doesn't fire a request;
  // typing always jumps back to page 1 of the new result set.
  useEffect(() => {
    const handle = setTimeout(() => {
      setPickerPage(1);
      setPickerSearch(pickerSearchInput.trim());
    }, 350);
    return () => clearTimeout(handle);
  }, [pickerSearchInput]);

  const pickerPageCount = Math.max(1, Math.ceil(pickerTotalCount / PRODUCT_PICKER_PAGE_SIZE));

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleProduct = (productId) => {
    setSettings(prev => {
      const current = new Set(prev.featured_product_ids || []);
      current.has(productId) ? current.delete(productId) : current.add(productId);
      return { ...prev, featured_product_ids: Array.from(current), show_all_products: false };
    });
  };

  const toggleCategory = (categoryName) => {
    setSettings(prev => {
      const current = new Set(prev.featured_category_names || []);
      current.has(categoryName) ? current.delete(categoryName) : current.add(categoryName);
      return { ...prev, featured_category_names: Array.from(current), show_all_products: false };
    });
  };

  const saveSettings = async () => {
    if (!storeId) {
      handleToast({ type: 'ERROR', message: 'Store ID not found' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...settings,
        store: storeId,
        discount_value: Number(settings.discount_value || 0),
        enable_global_discount: settings.discount_type !== 'none' && Number(settings.discount_value || 0) > 0,
      };

      if (settingsId) {
        const response = await Patch('storefront_settings', settingsId, payload);
        const data = response?.data?.data || response?.data;
        if (data?.id) setSettingsId(data.id);
      } else {
        const response = await Post('storefront_settings', payload);
        const data = response?.data?.data || response?.data;
        if (data?.id) setSettingsId(data.id);
      }

      handleToast({ type: 'SUCCESS', message: 'Storefront settings saved' });
      await fetchStorefrontSettings();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to save storefront settings';
      handleToast({ type: 'ERROR', message });
    } finally {
      setSaving(false);
    }
  };

  const normalizeDomainInput = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '';
    try {
      const parsed = new URL(raw.includes('://') ? raw : `https://${raw}`);
      return parsed.hostname.replace(/^www\./, '').replace(/\.$/, '');
    } catch (error) {
      return raw.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '').replace(/\.$/, '');
    }
  };

  const persistDomains = async (nextDomains) => {
    const payload = { store_id: String(storeId), urls: nextDomains };
    if (storeConfigId) {
      const response = await Put('ecom_store', storeConfigId, payload);
      const data = response?.data?.data || response?.data;
      if (data?.id) setStoreConfigId(data.id);
    } else {
      const response = await Post('ecom_store', payload);
      const data = response?.data?.data || response?.data;
      if (data?.id) setStoreConfigId(data.id);
    }
    setDomains(nextDomains);
  };

  const addDomain = async () => {
    const domain = normalizeDomainInput(newDomain);
    if (!domain) {
      handleToast({ type: 'ERROR', message: 'Please enter a valid domain' });
      return;
    }
    if (domains.includes(domain)) {
      handleToast({ type: 'ERROR', message: 'Domain already exists' });
      return;
    }
    if (!window.confirm(`Add ${domain} to this store?`)) return;

    setSaving(true);
    try {
      await persistDomains([...domains, domain]);
      setNewDomain('');
      handleToast({ type: 'SUCCESS', message: 'Domain added' });
      await fetchStoreDomains();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to add domain';
      handleToast({ type: 'ERROR', message });
    } finally {
      setSaving(false);
    }
  };

  const saveDomainEdit = async (oldDomain) => {
    const domain = normalizeDomainInput(editingDomainValue);
    if (!domain) {
      handleToast({ type: 'ERROR', message: 'Please enter a valid domain' });
      return;
    }
    if (domains.some(item => item !== oldDomain && item === domain)) {
      handleToast({ type: 'ERROR', message: 'Domain already exists' });
      return;
    }
    if (!window.confirm(`Change ${oldDomain} to ${domain}?`)) return;

    setSaving(true);
    try {
      await persistDomains(domains.map(item => item === oldDomain ? domain : item));
      setEditingDomain(null);
      setEditingDomainValue('');
      handleToast({ type: 'SUCCESS', message: 'Domain updated' });
      await fetchStoreDomains();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to update domain';
      handleToast({ type: 'ERROR', message });
    } finally {
      setSaving(false);
    }
  };

  const deleteDomain = async (domain) => {
    if (!window.confirm(`Delete ${domain} from this store?`)) return;

    setSaving(true);
    try {
      await persistDomains(domains.filter(item => item !== domain));
      handleToast({ type: 'SUCCESS', message: 'Domain deleted' });
      await fetchStoreDomains();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to delete domain';
      handleToast({ type: 'ERROR', message });
    } finally {
      setSaving(false);
    }
  };

  const panels = [
    { id: 'overview', label: 'Overview', icon: <FaStore /> },
    { id: 'domain', label: 'Domains', icon: <FaGlobe /> },
    { id: 'theme', label: 'Theme', icon: <FaPalette /> },
    { id: 'visibility', label: 'Products', icon: <FaBoxes /> },
    { id: 'discount', label: 'Discount', icon: <FaPercent /> },
    { id: 'login', label: 'Social Login', icon: <FaKey /> },
    { id: 'email', label: 'Email Settings', icon: <FaEnvelope /> },
    { id: 'content', label: 'Logo & Content', icon: <FaImage /> },
  ];

  return (
    <div className="storefront-studio">
      <div className="studio-hero" style={{ '--studio-primary': selectedTheme.primary, '--studio-accent': selectedTheme.accent }}>
        <div>
          <span className="studio-pill"><FaStore /> Storefront Studio</span>
          <h2>Control your online store from one place</h2>
          <p>Manage domains, theme, content, sections, discounts, products and categories that appear on your public ecommerce website.</p>
        </div>
        {!['domain', 'content', 'login', 'email'].includes(activePanel) && (
          <button className="studio-save-btn" onClick={saveSettings} disabled={saving || loading}>
            <FaSave /> {saving ? 'Saving...' : 'Save Storefront'}
          </button>
        )}
      </div>

      <div className="studio-layout">
        <aside className="studio-sidebar">
          {panels.map(panel => (
            <button
              key={panel.id}
              className={`studio-nav-btn ${activePanel === panel.id ? 'active' : ''}`}
              onClick={() => setActivePanel(panel.id)}
            >
              {panel.icon}
              <span>{panel.label}</span>
            </button>
          ))}
        </aside>

        <main className="studio-main">
          {activePanel === 'overview' && (
            <div className="studio-grid">
              <section className="studio-card studio-card-wide">
                <div className="studio-card-title">
                  <FaBullhorn />
                  <div>
                    <h3>Announcement Bar</h3>
                    <p>Show a short offer or message at the top of the storefront.</p>
                  </div>
                </div>
                <input
                  value={settings.announcement_text || ''}
                  onChange={(e) => updateSetting('announcement_text', e.target.value)}
                  placeholder="Free delivery today, 10% off selected products..."
                />
              </section>

              <section className="studio-card">
                <h3>Header CTA</h3>
                <input
                  value={settings.header_cta_text || ''}
                  onChange={(e) => updateSetting('header_cta_text', e.target.value)}
                  placeholder="Button text"
                />
                <input
                  value={settings.header_cta_link || ''}
                  onChange={(e) => updateSetting('header_cta_link', e.target.value)}
                  placeholder="/products or /checkout"
                />
              </section>

              <section className="studio-card">
                <h3>Website Sections</h3>
                {[
                  ['show_category_section', 'Shop by categories'],
                  ['show_deal_section', 'Deals of the week'],
                  ['show_about_section', 'About section'],
                  ['show_contact_section', 'Contact section'],
                ].map(([key, label]) => (
                  <label key={key} className="studio-switch">
                    <span>{label}</span>
                    <input
                      type="checkbox"
                      checked={Boolean(settings[key])}
                      onChange={(e) => updateSetting(key, e.target.checked)}
                    />
                  </label>
                ))}
              </section>
            </div>
          )}

          {activePanel === 'domain' && (
            <div className="studio-domain-card">
              <div className="studio-panel-copy compact">
                <h3>Website URLs</h3>
                <p>Add the domain where this store will be published. Each domain can belong to only one store.</p>
              </div>

              <div className="domain-add-row">
                <input
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                  placeholder="mystore.com"
                />
                <button onClick={addDomain} disabled={saving}>Add Domain</button>
              </div>

              <div className="domain-simple-list">
                {domains.length === 0 && <div className="domain-empty">No domains added yet.</div>}
                {domains.map(domain => (
                  <div className="domain-row" key={domain}>
                    {editingDomain === domain ? (
                      <>
                        <input
                          value={editingDomainValue}
                          onChange={(e) => setEditingDomainValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveDomainEdit(domain);
                            if (e.key === 'Escape') {
                              setEditingDomain(null);
                              setEditingDomainValue('');
                            }
                          }}
                          autoFocus
                        />
                        <button onClick={() => saveDomainEdit(domain)} disabled={saving}>Save</button>
                        <button
                          className="domain-light-btn"
                          onClick={() => {
                            setEditingDomain(null);
                            setEditingDomainValue('');
                          }}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <span>{domain}</span>
                        <button
                          className="domain-light-btn"
                          onClick={() => {
                            setEditingDomain(domain);
                            setEditingDomainValue(domain);
                          }}
                          disabled={saving}
                        >
                          Edit
                        </button>
                        <button className="domain-delete-btn" onClick={() => deleteDomain(domain)} disabled={saving}>
                          <FaTrash /> Delete
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activePanel === 'theme' && (
            <div className="theme-panel">
              <div className="studio-panel-copy compact">
                <h3>Storefront type</h3>
                <p>Choose the customer experience for this website. Your color palette is selected separately below.</p>
              </div>
              <div className="theme-type-grid">
                {THEME_TYPES.map(type => (
                  <button
                    key={type.key}
                    className={`theme-type-card ${settings.theme_type === type.key ? 'selected' : ''}`}
                    onClick={() => updateSetting('theme_type', type.key)}
                  >
                    <strong>{type.name}</strong>
                    <small>{type.description}</small>
                    {settings.theme_type === type.key && <FaCheck className="theme-check" />}
                  </button>
                ))}
              </div>
              <div className="studio-panel-copy compact theme-palette-heading">
                <h3>Theme color</h3>
                <p>Choose the brand color applied across the selected storefront type.</p>
              </div>
              <div className="theme-grid">
                {THEMES.map(theme => (
                  <button
                    key={theme.key}
                    className={`theme-card ${settings.theme_key === theme.key ? 'selected' : ''}`}
                    onClick={() => updateSetting('theme_key', theme.key)}
                    style={{ '--theme-primary': theme.primary, '--theme-accent': theme.accent, '--theme-bg': theme.bg }}
                  >
                    <span className="theme-swatch" />
                    <strong>{theme.name}</strong>
                    <small>{theme.primary} / {theme.accent}</small>
                    {settings.theme_key === theme.key && <FaCheck className="theme-check" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {activePanel === 'visibility' && (
            <div className="studio-grid">
              <section className="studio-card studio-card-wide">
                <label className="studio-switch strong">
                  <span>Show all store products</span>
                  <input
                    type="checkbox"
                    checked={Boolean(settings.show_all_products)}
                    onChange={(e) => updateSetting('show_all_products', e.target.checked)}
                  />
                </label>
                <p className="studio-hint">Turn this off to publish only selected products or categories.</p>
              </section>

              <section className="studio-card">
                <h3><FaTags /> Categories to show</h3>
                <div className="chip-list">
                  {categories.map(category => (
                    <button
                      key={category}
                      className={(settings.featured_category_names || []).includes(category) ? 'selected' : ''}
                      onClick={() => toggleCategory(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </section>

              <section className="studio-card studio-card-products">
                <h3><FaBoxes /> Products to show</h3>
                <input
                  type="text"
                  className="studio-product-search"
                  placeholder="Search products by name..."
                  value={pickerSearchInput}
                  onChange={(e) => setPickerSearchInput(e.target.value)}
                />
                <div className="product-pick-list">
                  {pickerLoading ? (
                    <p className="studio-hint">Loading products...</p>
                  ) : pickerProducts.length === 0 ? (
                    <p className="studio-hint">No products found{pickerSearch ? ` for "${pickerSearch}"` : ''}.</p>
                  ) : (
                    pickerProducts.map(product => {
                      const productId = getProductId(product);
                      return (
                        <label key={productId} className="product-pick-row">
                          <input
                            type="checkbox"
                            checked={(settings.featured_product_ids || []).includes(productId)}
                            onChange={() => toggleProduct(productId)}
                          />
                          <span>{getProductName(product)}</span>
                          <small>{getProductCategory(product)}</small>
                        </label>
                      );
                    })
                  )}
                </div>
                {pickerTotalCount > PRODUCT_PICKER_PAGE_SIZE && (
                  <div className="studio-pagination">
                    <button
                      type="button"
                      onClick={() => setPickerPage(p => Math.max(1, p - 1))}
                      disabled={pickerPage <= 1 || pickerLoading}
                    >
                      Previous
                    </button>
                    <span>Page {pickerPage} of {pickerPageCount} &middot; {pickerTotalCount} products</span>
                    <button
                      type="button"
                      onClick={() => setPickerPage(p => Math.min(pickerPageCount, p + 1))}
                      disabled={pickerPage >= pickerPageCount || pickerLoading}
                    >
                      Next
                    </button>
                  </div>
                )}
              </section>
            </div>
          )}

          {activePanel === 'discount' && (
            <div className="studio-grid">
              <section className="studio-card studio-card-wide">
                <h3>Global Website Discount</h3>
                <p className="studio-hint">Discount applies only when final selling price remains equal to or above purchase price. Products without safe cost data keep normal price.</p>
                <div className="studio-form-row">
                  <select value={settings.discount_type} onChange={(e) => updateSetting('discount_type', e.target.value)}>
                    <option value="none">No discount</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                  <input
                    type="number"
                    min="0"
                    value={settings.discount_value}
                    onChange={(e) => updateSetting('discount_value', e.target.value)}
                    placeholder="Discount value"
                  />
                  <input
                    value={settings.discount_label || ''}
                    onChange={(e) => updateSetting('discount_label', e.target.value)}
                    placeholder="Label, e.g. Online special"
                  />
                </div>
              </section>
            </div>
          )}

          {activePanel === 'login' && (
            <div className="studio-grid">
              <section className="studio-card studio-card-wide">
                <div className="studio-card-title">
                  <FaKey />
                  <div>
                    <h3>Social Login</h3>
                    <p>Let customers sign in to your storefront with their Google or Facebook account instead of creating a password.</p>
                  </div>
                </div>
              </section>

              <section className="studio-card">
                <div className="studio-card-title">
                  <FaGoogle />
                  <div>
                    <h3>Google</h3>
                  </div>
                </div>
                <label className="studio-switch strong">
                  <span>Allow "Continue with Google"</span>
                  <input
                    type="checkbox"
                    checked={Boolean(socialSettings.google_enabled)}
                    onChange={(e) => updateSocialSetting('google_enabled', e.target.checked)}
                  />
                </label>
                <p className="studio-hint">Leave the client ID/secret blank to use the platform's default Google app.</p>
                <input
                  value={socialSettings.google_client_id || ''}
                  onChange={(e) => updateSocialSetting('google_client_id', e.target.value)}
                  placeholder="Google Client ID (optional)"
                />
                <input
                  type="password"
                  value={socialSettings.google_client_secret || ''}
                  onChange={(e) => updateSocialSetting('google_client_secret', e.target.value)}
                  placeholder={socialSettings.has_google_secret ? 'Client secret saved - enter a new one to replace it' : 'Google Client Secret (optional)'}
                />
              </section>

              <section className="studio-card">
                <div className="studio-card-title">
                  <FaFacebook />
                  <div>
                    <h3>Facebook</h3>
                  </div>
                </div>
                <label className="studio-switch strong">
                  <span>Allow "Continue with Facebook"</span>
                  <input
                    type="checkbox"
                    checked={Boolean(socialSettings.facebook_enabled)}
                    onChange={(e) => updateSocialSetting('facebook_enabled', e.target.checked)}
                  />
                </label>
                <p className="studio-hint">Leave these blank to use the platform's default Facebook app.</p>
                <input
                  value={socialSettings.facebook_client_id || ''}
                  onChange={(e) => updateSocialSetting('facebook_client_id', e.target.value)}
                  placeholder="Facebook App ID (optional)"
                />
                <input
                  type="password"
                  value={socialSettings.facebook_client_secret || ''}
                  onChange={(e) => updateSocialSetting('facebook_client_secret', e.target.value)}
                  placeholder={socialSettings.has_facebook_secret ? 'App secret saved - enter a new one to replace it' : 'Facebook App Secret (optional)'}
                />
                <input
                  value={socialSettings.facebook_redirect_uri || ''}
                  onChange={(e) => updateSocialSetting('facebook_redirect_uri', e.target.value)}
                  placeholder="Facebook OAuth redirect URI (optional override)"
                />
              </section>

              <section className="studio-card studio-card-wide">
                <button className="studio-save-btn" onClick={saveSocialSettings} disabled={socialSaving}>
                  <FaSave /> {socialSaving ? 'Saving...' : 'Save Social Login'}
                </button>
              </section>
            </div>
          )}

          {activePanel === 'email' && (
            <div className="studio-grid">
              <section className="studio-card studio-card-wide">
                <div className="studio-card-title">
                  <FaEnvelope />
                  <div>
                    <h3>Email Settings</h3>
                    <p>Set up a different sending account for each purpose below - password resets, order/customer communication, or one general account for everything. Anything you leave off uses the platform's shared account instead, up to a free allowance.</p>
                  </div>
                </div>
                <p className="studio-hint">
                  <strong>{emailUsage.emails_remaining} of {emailUsage.free_tier_limit} free platform emails remaining</strong> ({emailUsage.emails_sent} used so far). This is only used for purposes you haven't configured your own account for below.
                </p>
              </section>

              {emailPurposes.map((row) => {
                const purposeMeta = EMAIL_PURPOSES.find(p => p.value === row.purpose);
                const isGmail = row.provider === 'gmail';
                return (
                  <section className="studio-card studio-card-wide" key={row.purpose}>
                    <div className="studio-card-title">
                      <FaKey />
                      <div>
                        <h3>{purposeMeta?.label || row.purpose}</h3>
                        <p>{purposeMeta?.hint}</p>
                      </div>
                    </div>

                    <label className="studio-switch strong">
                      <span>Use my own account for this</span>
                      <input
                        type="checkbox"
                        checked={Boolean(row.enabled)}
                        onChange={(e) => updateEmailPurpose(row.purpose, 'enabled', e.target.checked)}
                      />
                    </label>

                    <div className="studio-form-row">
                      <label className="studio-switch">
                        <span>Gmail</span>
                        <input
                          type="radio"
                          name={`provider-${row.purpose}`}
                          checked={row.provider === 'gmail'}
                          onChange={() => updateEmailPurpose(row.purpose, 'provider', 'gmail')}
                        />
                      </label>
                      <label className="studio-switch">
                        <span>Custom SMTP</span>
                        <input
                          type="radio"
                          name={`provider-${row.purpose}`}
                          checked={row.provider === 'custom'}
                          onChange={() => updateEmailPurpose(row.purpose, 'provider', 'custom')}
                        />
                      </label>
                    </div>

                    {isGmail ? (
                      <>
                        <p className="studio-hint">Use a Gmail address and an app password (not your regular Gmail password - generate one from your Google Account's App Passwords page).</p>
                        <input
                          value={row.smtp_username || ''}
                          onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_username', e.target.value)}
                          placeholder="Gmail address, e.g. store@gmail.com"
                        />
                        <input
                          type="password"
                          value={row.smtp_password || ''}
                          onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_password', e.target.value)}
                          placeholder={row.has_smtp_password ? 'App password saved - enter a new one to replace it' : 'Gmail app password'}
                        />
                      </>
                    ) : (
                      <>
                        <p className="studio-hint">Any other SMTP provider - Zoho, Outlook, Amazon SES, your own mail server, etc.</p>
                        <div className="studio-form-row">
                          <input
                            value={row.smtp_host || ''}
                            onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_host', e.target.value)}
                            placeholder="SMTP host, e.g. smtp.zoho.com"
                          />
                          <input
                            type="number"
                            min="1"
                            value={row.smtp_port}
                            onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_port', e.target.value)}
                            placeholder="Port, e.g. 587"
                          />
                        </div>
                        <label className="studio-switch">
                          <span>Use TLS</span>
                          <input
                            type="checkbox"
                            checked={Boolean(row.smtp_use_tls)}
                            onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_use_tls', e.target.checked)}
                          />
                        </label>
                        <label className="studio-switch">
                          <span>Use SSL</span>
                          <input
                            type="checkbox"
                            checked={Boolean(row.smtp_use_ssl)}
                            onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_use_ssl', e.target.checked)}
                          />
                        </label>
                        <input
                          value={row.smtp_username || ''}
                          onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_username', e.target.value)}
                          placeholder="SMTP username"
                        />
                        <input
                          type="password"
                          value={row.smtp_password || ''}
                          onChange={(e) => updateEmailPurpose(row.purpose, 'smtp_password', e.target.value)}
                          placeholder={row.has_smtp_password ? 'Password saved - enter a new one to replace it' : 'SMTP password'}
                        />
                      </>
                    )}

                    <div className="studio-form-row">
                      <input
                        value={row.from_email || ''}
                        onChange={(e) => updateEmailPurpose(row.purpose, 'from_email', e.target.value)}
                        placeholder="From email (optional, defaults to the account above)"
                      />
                      <input
                        value={row.from_name || ''}
                        onChange={(e) => updateEmailPurpose(row.purpose, 'from_name', e.target.value)}
                        placeholder="From name (optional, e.g. your store name)"
                      />
                    </div>

                    <button
                      className="studio-save-btn"
                      onClick={() => saveEmailPurpose(row.purpose)}
                      disabled={emailSavingPurpose === row.purpose}
                    >
                      <FaSave /> {emailSavingPurpose === row.purpose ? 'Saving...' : `Save ${purposeMeta?.label || row.purpose}`}
                    </button>
                  </section>
                );
              })}
            </div>
          )}

          {activePanel === 'content' && (
            <div className="studio-panel-block">
              <div className="studio-panel-copy">
                <h3>Logo, Hero, Footer and Contact</h3>
                <p>Use this detailed editor for uploadable logo and hero images, footer contact details, menu links, about and contact sections.</p>
                <button className="studio-inline-save" onClick={() => setActivePanel('overview')}>
                  Back to overview <FaArrowRight />
                </button>
              </div>
              <ContentManagement />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StorefrontStudio;
